import React, { useState, useEffect } from "react";
import { Layout, Space, Row, Descriptions, Divider } from "antd";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import AdditionalEntityDetails from "../../../components/specific/company/additionalEntityDetails";
import BankAccountMultiView from "../../../components/bankAccount/multiView";
import HideOnMobile from "../../../components/generic/hideOnMobile";
import BankAccountToEntitySetRelation from "../../../components/specific/company/holdingEntities/bankAccountToEntitySetRelation";
import BankingEnabledValue from "../../../components/specific/company/holdingEntities/bankingEnabledValue";
import { documentTitle } from "../../../components/header/documentTitle";
import NewBankAccountForEntity from "../../../components/bankAccount/newBankAccountForEntity";
import TitledSection from "../../../components/generic/titledSection";
import AuthorizedUsersTable from "../../../components/specific/entity/authorizedUsersTable";
import { Stack, Grid, Box } from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EntityPaymentsApplicationState from "../../../components/specific/company/holdingEntities/EntityPaymentsApplicationState";
import GenericTagChips from "../../../components/specific/customTags/genericTagChips";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";
import checkFlag from "../../../helpers/featureFlags";
import VerifyMicroDeps from "../../../components/bankAccount/modals/VerifyMicroDeps";
import CreateFundingSource from "../../../components/bankAccount/modals/CreateFundingSource";
import EntityPaymentsApplicationModal from "../../../components/specific/company/holdingEntities/EntityPaymentsApplicationModal";
import OwnershipStructure from "../../../components/specific/company/holdingEntities/OwnershipStructure";
import PermissionGate from "../../../helpers/PermissionGate";
import Button from '@mui/material/Button';

function HoldingEntity() {
  const [entityName, setEntityName] = useState("")
  const [entityAddress, setEntityAddress] = useState("")
  const [entityLegalStructure, setEntityLegalStructure] = useState("")
  const [entityOfficer, setEntityOfficer] = useState("")
  const [entityBusinessContact, setEntityBusinessContact] = useState("")

  const [isBankingEnabled, setIsBankingEnabled] = useState(undefined)
  const [isInProgress, setIsInProgress] = useState(undefined)
  const [editEntityURL, setEditEntityURL] = useState(undefined)
  const [newEntityURL, setNewEntityURL] = useState(undefined)
  const [authorizedUsers, setAuthorizedUsers] = useState([])
  const [ownerships, setOwnerships] = useState([])
  const [allEntities, setAllEntities] = useState([])

  const [bankAccountsPerPage, setBankAccountsPerPage] = useState({});
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(true);
  const [associatedBankAccounts, setAssociatedBankAccounts] = useState([]);
  const [pageCountExpected, setPageCountExpected] = useState(-1);
  const [pageCountReceived, setPageCountReceived] = useState(0);
  const [bankAccountInitialized, setBankAccountInitialized] = useState(false);

  const [additionalEntityData, setAdditionalEntityData] = useState({});

  const [customerId, setCustomerId] = useState("");
  const [entityPaymentsVerified, setEntityPaymentsVerified] = useState(false);
  const [customerApplicationModalVisible, setCustomerApplicationModalVisible] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState(0);
  const [fundingSourceApplicationVisible, setFundingSourceApplicationVisible] = useState(false);
  const [verifyMicroDepsVisible, setVerifyMicroDepsVisible] = useState(false);
  const [selectedBankAccountName, setSelectedBankAccountName] = useState("");
  const [fundingSourceId, setFundingSourceId] = useState("");
  const [fundingSourceIsVerified, setFundingSourceIsVerified] = useState(false);

  const entityId = window.location.pathname.split("holding-entities/details/000")[1];
  const [entityIsVerified, setEntityIsVerified] = useState(false)

  const isSoftwareCustomer = JSON.parse(localStorage.getItem("isSoftwareCustomer"))

  function stringJoin(arr, joinVal) {
    const filteredArr = arr.filter(entry => entry && entry.length > 0)
    if (filteredArr.length < 1) return ""
    return filteredArr.join(joinVal)
  }

  useEffect(() => {
    if (!fundingSourceId) {
      return;
    }
    const newAssociatedBankAccounts = associatedBankAccounts.map((bankAccount) => {
      if (bankAccount.id == selectedBankAccount) {
        bankAccount.dwolla_funding_source_id = fundingSourceId;
      }
      return bankAccount;
    })
    setAssociatedBankAccounts(newAssociatedBankAccounts);

  }, [fundingSourceId])

  useEffect(() => {
    if (!fundingSourceIsVerified) {
      return;
    }
    const newAssociatedBankAccounts = associatedBankAccounts.map((bankAccount) => {
      if (bankAccount.id == selectedBankAccount) {
        bankAccount.dwolla_funding_source_is_verified = fundingSourceIsVerified;
      }
      return bankAccount;
    })
    setAssociatedBankAccounts(newAssociatedBankAccounts);

  }, [fundingSourceIsVerified])

  useEffect(() => {
    const selectedAccount = associatedBankAccounts.find(account => account.id == selectedBankAccount);
    if (selectedAccount) {
      setSelectedBankAccountName(selectedAccount.description)
    }

  }, [selectedBankAccount])


  useEffect(() => {
    if (entityName) {
      documentTitle({ title: `Holding Entities | ${entityName}` });
    } else {
      documentTitle();
    }
  }, [entityName])

  useEffect(() => {
    const processResponse = (data) => {
      setEntityName(data.entity_info?.name);
      setAuthorizedUsers(data.authorized_users);
      setOwnerships(data.ownerships || []);

      if (!data.entity_info.address_one_liner.startsWith("Unknown")) {
        let address = data.entity_info.address_one_liner.replace(' Unknown,', '')
        setEntityAddress(address)
      }
      if (!data.entity_info.entity_type.startsWith("Unknown")) {
        setEntityLegalStructure(stringJoin([
          data.entity_info.state_of_incorporation,
          data.entity_info.entity_type
        ], " "))
      }
      setEntityOfficer(stringJoin([
        data.entity_info.officer?.fullName?.first,
        data.entity_info.officer?.fullName?.last
      ], " "))
      setEntityBusinessContact(stringJoin([
        data.entity_info.contact?.fullName?.first,
        data.entity_info.contact?.fullName?.last
      ], " "))

      setIsBankingEnabled(data.entity_info?.is_complete);
      setIsInProgress(data.entity_info?.in_progress);
      setEditEntityURL(data.entity_info?.edit_application_url);
      setNewEntityURL(data.entity_info?.new_application_url);
      setCustomerId(data.dwolla_customer_id);
      setEntityPaymentsVerified(Boolean(data.dwolla_customer_is_verified));
      setEntityIsVerified(data.dwolla_customer_is_verified)
    }

    backendGetRequestWithCache("/api/entity/", { params: { entity_id: entityId } },
      (cachedResponse) => {
        if (cachedResponse.data && !entityName.length) {
          processResponse(cachedResponse.data.data);
        }
      },
      (nonCachedResponse) => {
        if (nonCachedResponse.data) {
          processResponse(nonCachedResponse.data.data);
        }
      });

    backendGetRequestWithCache("/api/entity/additional-details/", { params: { entity_id: entityId } },
      (cachedResponse) => {
        if (cachedResponse.data && Object.keys(additionalEntityData).length === 0) {
          setAdditionalEntityData(cachedResponse.data.extra_data);
        }
      },
      (nonCachedResponse) => {
        if (nonCachedResponse.data) {
          setAdditionalEntityData(nonCachedResponse.data.extra_data);
        }
      });

    backendGetRequest("/api/bank-account/", { params: { entity_id: entityId, page_index: 0 } })
      .then((res) => {
        setPageCountExpected(prevState => res?.data?.total_pages);
        setBankAccountInitialized(true);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          let params = { entity_id: entityId, page_index: i };
          backendGetRequestWithCache("/api/bank-account/", { params: params },
            (cachedResponse) => {
              if (cachedResponse?.data) {
                setBankAccountsPerPage(prevState => {
                  if (!prevState[params.page_index]) {
                    return {
                      ...prevState,
                      [params.page_index]: cachedResponse?.data?.associated_bank_accounts,
                    }
                  }
                  return prevState;
                });

              }
            },
            (nonCachedResponse) => {
              if (nonCachedResponse?.data) {
                setBankAccountsPerPage(prevState => ({
                  ...prevState,
                  [params.page_index]: nonCachedResponse?.data?.associated_bank_accounts,
                }));
                setPageCountReceived(prevState => prevState + 1);
              }
            },
            () => { // error handling
              setPageCountExpected(prevState => prevState - 1);
            }
          );
        }
      })
    
    // Fetch all entities for the ownership dropdown
    backendGetRequestWithCache("/api/entity", { params: { 'page_index': 0 } },
      (cachedResponse) => {
        if (cachedResponse?.data && allEntities.length === 0) {
          const entities = cachedResponse?.data?.entities || [];
          setAllEntities(entities);
        }
      },
      (nonCachedResponse) => {
        if (nonCachedResponse?.data) {
          const entities = nonCachedResponse?.data?.entities || [];
          setAllEntities(entities);
        }
      });
  }, []);


  useEffect(() => {
    if (bankAccountsPerPage && typeof bankAccountsPerPage === 'object') {
      const flattenedAccounts = Object.values(bankAccountsPerPage).flat();
      setAssociatedBankAccounts(flattenedAccounts);
    }
  }, [bankAccountsPerPage]);

  useEffect(() => {
    if (!bankAccountInitialized) return;
    const isLoading = pageCountExpected > pageCountReceived;
    setLoadingBankAccounts(prevState => isLoading);
  }, [bankAccountInitialized, pageCountExpected, pageCountReceived, associatedBankAccounts, associatedBankAccounts.length]);


  const editBankAccount = (accountId, newDescription) => {
    setAssociatedBankAccounts(prevState => prevState.map((account) => {
      if (account.id === accountId) {
        return { ...account, description: newDescription };
      }
      return account;
    }));
  };

  const addBankAccounts = (newBankAccounts) => {
    setAssociatedBankAccounts(prevState => [...prevState, ...(newBankAccounts || [])]);
  };

  const deleteBankAccount = (accountId) => {
    setAssociatedBankAccounts(prevState => prevState.filter((account) => account.id !== accountId));
  }

  function getValueFromOptions(options, key, defaultValue) {
    // helper function to allow empty value in toPrice
    var ret = options[key]
    if (ret === undefined) {
      return defaultValue
    }
    return ret;
  }

  const addNewBankAccounts = (newBankAccounts) => {
    setAssociatedBankAccounts(prevState => [...prevState, ...newBankAccounts]);
  }

  const onManualBalanceUpdateSuccess = (response) => {
    setAssociatedBankAccounts((prevState) => {
      return prevState.map((acc) => {
        if (acc.id === response.data.id && acc.account.account_source === response.data.account.account_source) {
          const newAcc = {
            ...acc,
            account: {
              ...acc.account,
              current_balance: response.data.account.current_balance,
              available_balance: response.data.account.available_balance,
              aggregated_balance: response.data.account.aggregated_balance,
              current_balance_raw: response.data.account.current_balance_raw,
              available_balance_raw: response.data.account.available_balance_raw,
              aggregated_balance_raw: response.data.account.aggregated_balance_raw
            }
          }
          return newAcc
        }
        return acc;
      })
    })
  };

  const handleOwnershipUpdate = (updatedEntity) => {
    // Update entity name if changed
    if (updatedEntity.entity_info?.name) {
      setEntityName(updatedEntity.entity_info.name);
    }
    // Update ownership relationships
    if (updatedEntity.ownerships) {
      setOwnerships(updatedEntity.ownerships);
    }
  };

  const formatHeaderBodyEntry = (title, val) => {
    return val && <Grid item>
      <Stack direciton="column">
        <Typography sx={{ mb: 0, mr: 2 }}
          variant="overline"
          fontSize={12}
          color="secondary"
          noWrap={true}>{title}</Typography>
        <Typography sx={{ mt: -1, mr: 3 }}>{val}</Typography>
      </Stack>
    </Grid>

  }
  return (
    <OuterBox>
      <HeaderBox
        title={entityName}
        actions={
          <HideOnMobile>
            <Stack spacing={1} direction="row">
              {isBankingEnabled &&
                <PermissionGate
                  requiredRole='dashboardEditor'
                  toolTipText='Only admins and dashboard editors may open bank accounts'
                  readOnly={
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ fontSize: 14, width: '200px', textTransform: 'none' }}
                      disabled
                    >
                      Open Bank Account(s)
                    </Button>
                  }
                >
                  <NewBankAccountForEntity
                    title={"Open Bank Account(s) for " + entityName}
                    entityId={entityId}
                    entityName={entityName}
                    addNewBankAccounts={addNewBankAccounts}
                    buttonVariant={isBankingEnabled ? "contained" : "outlined"}
                    buttonColor={isBankingEnabled ? "primary" : "secondary"}
                  />
                </PermissionGate>
              }
              <PermissionGate
                requiredRole='dashboardEditor'
                toolTipText='Only admins and dashboard editors may assign bank accounts to holding entities'
                readOnly={
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 14, width: '200px', textTransform: 'none' }} style={{ marginRight: "10px" }}
                    disabled
                  >
                    Assign Bank Account(s)
                  </Button>
                }
              >
                <BankAccountToEntitySetRelation
                  entityId={entityId}
                  entityName={entityName}
                  initializeBankAccounts={true}
                  addBankAccounts={addBankAccounts}
                  buttonVariant={isBankingEnabled ? "outlined" : "contained"}
                  buttonColor={isBankingEnabled ? "secondary" : "primary"}
                />
              </PermissionGate>
            </Stack>
          </HideOnMobile>}
        body={
          <Stack direction="column">
            <Stack direction="row">
              {formatHeaderBodyEntry("Mailing Address", entityAddress)}
              {formatHeaderBodyEntry("Legal Structure", entityLegalStructure)}
              {formatHeaderBodyEntry("Officer", entityOfficer)}
              {formatHeaderBodyEntry("Business Contact", entityBusinessContact)}
              {formatHeaderBodyEntry(
                <Tooltip
                  title={
                    <Typography>
                      Arpari bank accounts can be opened for 'Bank Enabled'
                      accounts. Enable by filling out an application.
                    </Typography>
                  }
                >
                  <Typography variant="overline" fontSize={12} sx={{ mb: 0, mr: 2 }} color="secondary" noWrap={true}>Banking Enabled

                    <InfoOutlinedIcon />
                  </Typography>
                </Tooltip>,
                <BankingEnabledValue
                  isBankingEnabled={isBankingEnabled}
                  isInProgress={isInProgress}
                  editEntityURL={editEntityURL}
                  newEntityURL={newEntityURL}
                />)}
              {(checkFlag('paymentsV2') && isSoftwareCustomer) && formatHeaderBodyEntry(
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Typography variant="primary">Payments Enabled</Typography>
                </Stack>,
                <EntityPaymentsApplicationState
                  isVerified={entityIsVerified}
                  initialCustomerId={customerId}
                  entityId={entityId}
                />
              )}
            </Stack>
            <Stack direction="row"
              sx={{
                justifyContent: "flex-start",
                alignItems: "flex-end",
              }}>

              <GenericTagChips genericMap={additionalEntityData} />

              <HideOnMobile>
                <AdditionalEntityDetails
                  additionalEntityData={additionalEntityData}
                  entityId={entityId}
                  onSubmit={(data) => setAdditionalEntityData(data)}
                />
              </HideOnMobile>

            </Stack>

          </Stack>}
      />

      <OwnershipStructure
        ownerships={ownerships}
        entityId={entityId}
        entityName={entityName}
        entityData={allEntities}
        onOwnershipUpdate={handleOwnershipUpdate}
      />

      {authorizedUsers.length > 0 &&

        <AuthorizedUsersTable
          authorizedUsersData={authorizedUsers}
        />
      }

      <BankAccountMultiView
        allowActions={true}
        accountsInfo={associatedBankAccounts}
        setAccounts={setAssociatedBankAccounts}
        updateBalanceSuccess={onManualBalanceUpdateSuccess}
        accountTypeTitle="Bank Accounts"
        newAccountType="checking"
        accountOwnerAppName="landlord"
        deleteBankAccount={deleteBankAccount}
        editBankAccount={editBankAccount}
        reassignBankAccount={(updatedBankAccount) =>
          setAssociatedBankAccounts(prevState =>
            prevState.map(account =>
              account.id === updatedBankAccount.id ? { ...updatedBankAccount } : account))}
        loadingTable={loadingBankAccounts}
        entityPaymentsVerified={entityPaymentsVerified}
        setSelectedBankAccount={setSelectedBankAccount}
        entityId={entityId}
        displayPaymentsVerifiedColumn={isSoftwareCustomer}
      />
    </OuterBox>
  );
}

export default HoldingEntity;
